/**
 * Иллюстрации карточек режимов (встроенный SVG, цвета — из темы через CSS-переменные).
 *   blind3 — три карты рубашкой вверх над приглушённым графиком: состав скрыт;
 *   spx    — открытый свечной график с плашкой тикера: инструмент и цена известны;
 *   иначе  — нейтральный график (новый режим до появления своей иллюстрации).
 */
const W = 320;
const H = 180;

export function VariantArt({ id, symbol }: { id: string; symbol: string | null }) {
  return (
    <svg className="variant-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <defs>
        <pattern id={`grid-${id}`} width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0H0V20" fill="none" stroke="var(--art-grid)" strokeWidth="1" />
        </pattern>
        <radialGradient id={`glow-${id}`} cx="50%" cy="40%" r="70%">
          <stop offset="0%" stopColor="var(--art-glow)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="var(--art-glow)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width={W} height={H} fill="var(--art-bg)" />
      <rect width={W} height={H} fill={`url(#grid-${id})`} />
      <rect width={W} height={H} fill={`url(#glow-${id})`} />
      {id === 'blind3' ? <BlindThree /> : id === 'spx' ? <OpenChart symbol={symbol} /> : <Line opacity={0.8} />}
    </svg>
  );
}

/** Приглушённая ломаная — «график без шкалы». */
function Line({ opacity }: { opacity: number }) {
  const pts = [8, 120, 40, 104, 70, 112, 100, 88, 130, 96, 160, 70, 190, 82, 220, 60, 250, 72, 280, 48, 312, 58];
  const d = pts.reduce((acc, v, i) => acc + (i % 2 === 0 ? `${i ? ' L' : 'M'}${v}` : ` ${v}`), '');
  return <path d={d} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" opacity={opacity} />;
}

function BlindThree() {
  const cards = [
    { x: 96, r: -14 },
    { x: 160, r: 0 },
    { x: 224, r: 14 },
  ];
  return (
    <g>
      <g opacity={0.35}>
        <Line opacity={1} />
      </g>
      {cards.map((c, i) => (
        <g key={i} transform={`translate(${c.x} 98) rotate(${c.r}) translate(-30 -44)`}>
          <rect x="3" y="5" width="60" height="88" rx="8" fill="#000" opacity="0.28" />
          <rect width="60" height="88" rx="8" fill="var(--art-card)" stroke="var(--accent)" strokeWidth="2" />
          <rect x="6" y="6" width="48" height="76" rx="5" fill="none" stroke="var(--accent)" strokeOpacity="0.45" strokeDasharray="3 3" />
          <text x="30" y="56" textAnchor="middle" fontSize="34" fontWeight="700" fill="var(--accent)" fontFamily="var(--font)">
            ?
          </text>
        </g>
      ))}
    </g>
  );
}

function OpenChart({ symbol }: { symbol: string | null }) {
  // свечи: o, c, h, l в координатах SVG (меньше — выше)
  const candles = [
    [136, 122, 118, 140],
    [122, 132, 118, 136],
    [132, 114, 110, 135],
    [114, 126, 110, 130],
    [126, 104, 100, 129],
    [104, 96, 92, 110],
    [96, 110, 92, 114],
    [110, 86, 82, 113],
    [86, 98, 82, 102],
    [98, 66, 60, 100],
  ];
  const step = 26;
  const x0 = 40;
  return (
    <g>
      <line x1="16" x2={W - 16} y1="132" y2="132" stroke="var(--muted)" strokeDasharray="4 4" strokeWidth="1" opacity="0.6" />
      {candles.map(([o, c, h, l], i) => {
        const x = x0 + i * step;
        const up = c! < o!;
        const color = up ? 'var(--up)' : 'var(--down)';
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={h} y2={l} stroke={color} strokeWidth="2" />
            <rect x={x - 9} y={Math.min(o!, c!)} width="18" height={Math.max(4, Math.abs(c! - o!))} rx="2" fill={color} />
          </g>
        );
      })}
      {symbol && (
        <g transform="translate(18 18)">
          <rect width={symbol.length * 13 + 34} height="30" rx="6" fill="var(--art-card)" stroke="var(--accent)" strokeWidth="1.5" />
          <circle cx="15" cy="15" r="5" fill="var(--up)" />
          <text x="28" y="21" fontSize="17" fontWeight="700" fill="var(--text)" fontFamily="var(--font-mono)">
            {symbol}
          </text>
        </g>
      )}
    </g>
  );
}
